import LockIcon from '@mui/icons-material/Lock';
import { Alert, Chip, Tooltip } from '@mui/material';
import {
  Datagrid, Edit, List, NumberField, NumberInput, SimpleForm, TextField, TextInput, TopToolbar, useRecordContext, type RaRecord
} from 'react-admin';
import { BackLink } from './back';

type Group = RaRecord & {
  name: string;
  nameLocked: boolean;
  originalDiscountPercent: number;
  renewalDiscountPercent: number;
  memberCount: number;
};

/**
 * User groups: the discounts.
 *
 * Two of these are load-bearing by NAME. The storefront matches 'siemens' and
 * 'spp' as strings to exclude them from sale pricing and to give them
 * different order screens, so renaming one silently changes what those
 * customers pay. The API refuses it; this makes the refusal visible before
 * anyone types.
 *
 * The percentages are labelled as discounts, not prices, because the columns
 * behind them (original_price, renewal_price) are named misleadingly and seven
 * of the eleven groups are NAMED after their own values - "o: 3, r: 10" - which
 * go stale the moment someone edits them.
 */
function NameField() {
  const record = useRecordContext<Group>();
  if (record == null) return null;

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      {record.name}
      {record.nameLocked && (
        <Tooltip title="Web hľadá túto skupinu podľa názvu pri cenách aj pri objednávke, preto sa nedá premenovať.">
          <LockIcon fontSize="small" color="disabled" />
        </Tooltip>
      )}
    </span>
  );
}

function MembersField() {
  const record = useRecordContext<Group>();
  if (record == null) return null;
  return <Chip size="small" variant="outlined" label={`${record.memberCount} ${record.memberCount === 1 ? 'user' : 'users'}`} />;
}

export function GroupList() {
  return (
    <List sort={{ field: 'id', order: 'ASC' }} pagination={false} title="Skupiny používateľov">
      <Datagrid rowClick="edit" bulkActionButtons={false}>
        <NameField />
        <NumberField source="originalDiscountPercent" label="Zľava na originál %" />
        <NumberField source="renewalDiscountPercent" label="Zľava na renováciu %" />
        <MembersField />
      </Datagrid>
    </List>
  );
}

function LockNotice() {
  const record = useRecordContext<Group>();
  if (record?.nameLocked !== true) return null;

  return (
    <Alert severity="info" sx={{ mb: 2 }}>
      Názov <strong>{record.name}</strong> sa nedá zmeniť. Web ho hľadá ako text, aby túto skupinu
      vynechal z akciových cien a aby jej zobrazil inú objednávkovú obrazovku — premenovanie by teda
      zmenilo, koľko títo zákazníci platia, a nič by pritom viditeľne nezlyhalo.
    </Alert>
  );
}

function NameInput() {
  const record = useRecordContext<Group>();
  return <TextInput source="name" disabled={record?.nameLocked === true} fullWidth />;
}

export function GroupEdit() {
  return (
    <Edit title="Skupina používateľov" actions={<TopToolbar sx={{ justifyContent: 'flex-start' }}><BackLink /></TopToolbar>}>
      <SimpleForm>
        <LockNotice />
        <NameInput />
        <NumberInput
          source="originalDiscountPercent"
          label="Zľava na originál %"
          helperText="Platí len na originálne náplne. Tlačiarne skupinovú zľavu nikdy nedostanú."
          min={0}
          max={100}
        />
        <NumberInput
          source="renewalDiscountPercent"
          label="Zľava na renováciu %"
          helperText="Použije sa namiesto zľavy vyššie, keď je produkt renovovaný."
          min={0}
          max={100}
        />
      </SimpleForm>
    </Edit>
  );
}
